import { getErrorMessage } from '@/lib/functions';
import { useTranslation } from 'react-i18next';
﻿import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useProfile } from '@/contexts/ProfileContext';
import type { WalletTransaction } from '@/lib/database.types';
import { listWalletTransactions, myFreeRemaining } from '@/lib/agreements';
import { formatAmount, formatDateTime, formatSignedAmount, walletKindLabel } from '@/lib/format';
import { ErrorText } from '@/components/ErrorText';

export default function WalletScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { profile, refresh } = useProfile();

  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [freeRemaining, setFreeRemaining] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [rows, free] = await Promise.all([listWalletTransactions(), myFreeRemaining(), refresh()] as const);
      setTransactions(rows);
      setFreeRemaining(free);
    } catch (e) {
      setError(getErrorMessage(e, t('common.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  async function onRefresh() {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }

  const balance = profile ? Number(profile.wallet_balance) : 0;

  return (
    <View style={[styles.container, { paddingTop: insets.top + spacing.lg }]}>
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('wallet.title')}</Text>
        <View style={styles.spacer} />
      </View>

      <View style={styles.balanceCard}>
        <View style={styles.balanceHeader}>
          <Ionicons name="create-outline" size={32} color={colors.primary} />
          <View style={styles.balanceTextWrap}>
            <Text style={styles.balanceLabel}>{t('wallet.balance_label')}</Text>
            <Text style={styles.balanceValue}>{Math.floor(balance)}</Text>
          </View>
        </View>
        <Text style={styles.balanceHint}>
          {t('wallet.balance_hint')}
        </Text>
        <Button
          title={t('wallet.action_topup')}
          icon="color-fill-outline"
          variant="secondary"
          onPress={() => router.push('/(app)/wallet/topup')}
        />
      </View>

      {freeRemaining !== null && freeRemaining > 0 && (
        <View style={styles.bonusCard}>
          <Ionicons name="gift-outline" size={24} color={colors.primary} />
          <View style={styles.bonusInfo}>
            <Text style={styles.bonusLabel}>{t('wallet.freeAgreements')}</Text>
            <Text style={styles.bonusValue}>{freeRemaining}</Text>
          </View>
        </View>
      )}

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={transactions}
          keyExtractor={(t) => t.id}
          contentContainerStyle={styles.list}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
          ListHeaderComponent={
            transactions.length > 0 ? <Text style={styles.subtitle}>{t('wallet.transactions')}</Text> : null
          }
          ListEmptyComponent={
            <View style={styles.center}>
              <Text style={styles.emptyTitle}>{t('wallet.empty_title')}</Text>
              <Text style={styles.emptyText}>
                {t('wallet.empty_body')}
              </Text>
            </View>
          }
          renderItem={({ item }) => {
            const credit = item.amount >= 0;
            return (
              <View style={styles.card}>
                <View style={styles.cardLeft}>
                  <Text style={styles.cardTitle}>{walletKindLabel(item.kind)}</Text>
                  <Text style={styles.cardDate}>{formatDateTime(item.created_at)}</Text>
                </View>
                <View style={styles.cardRight}>
                  <Text style={[styles.cardAmount, { color: credit ? colors.success : colors.danger }]}>
                    {formatSignedAmount(item.amount, 'EUR')}
                  </Text>
                  <Text style={styles.cardBalance}>
                    {t('wallet.balance_after', { amount: formatAmount(item.balance_after, 'EUR') })}
                  </Text>
                </View>
              </View>
            );
          }}
        />
      )}

      <ErrorText>{error}</ErrorText>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
      paddingHorizontal: spacing.lg,
    },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing.md,
    },
    back: {
      color: colors.primaryLight,
      fontSize: fontSize.md,
      fontWeight: '600',
      width: 70,
    },
    title: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    spacer: { width: 70 },
    balanceCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.lg,
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    balanceHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
    },
    balanceTextWrap: {
      flexShrink: 1,
    },
    balanceLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      textTransform: 'uppercase',
      letterSpacing: 1,
      flexShrink: 1,
    },
    balanceValue: {
      color: colors.text,
      fontSize: fontSize.xxl,
      fontWeight: '800',
    },
    balanceHint: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 19,
    },
    bonusCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: radius.lg,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
    },
    bonusInfo: { flex: 1 },
    bonusLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    bonusValue: {
      color: colors.primary,
      fontSize: fontSize.xl,
      fontWeight: '700',
    },
    subtitle: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      marginBottom: spacing.sm,
    },
    list: {
      gap: spacing.md,
      paddingVertical: spacing.sm,
      flexGrow: 1,
    },
    card: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: spacing.md,
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
    },
    cardLeft: { flex: 1, gap: 2 },
    cardTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    cardDate: { color: colors.textMuted, fontSize: fontSize.sm },
    cardRight: { alignItems: 'flex-end', gap: 2 },
    cardAmount: { fontSize: fontSize.md, fontWeight: '800' },
    cardBalance: { color: colors.textMuted, fontSize: fontSize.sm },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.xxl,
    },
    emptyTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '600' },
    emptyText: { color: colors.textMuted, fontSize: fontSize.md, textAlign: 'center' },
    error: { color: colors.danger, fontSize: fontSize.sm, textAlign: 'center', paddingVertical: spacing.sm },
  });
}
